import { Flex } from "@chakra-ui/react";
import { neutral } from "@intentsify/brand";
import { isPopulatedArray } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { ErrorBoundary, Loader, NoData } from "components";
import { useCallback, useMemo } from "react";
import { byLook } from "theme/look";
import {
  BrandChart,
  ChartExportSvg,
  LineLegend,
  useBrandChartTheme,
} from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import { LegacyScatter } from "./legacy/Scatter";
import {
  buildScatterDefinition,
  getScatterLegend,
} from "./Scatter.definition";
import { ScatterProps } from "./Scatter.types";

export type { ScatterItem, ScatterProps } from "./Scatter.types";

const BrandScatter = ({
  data,
  id,
  isLoading,
  exportMode,
  title,
  stretch = false,
  minHeight = MIN_HEIGHT,
  noDataMessage,
  xAxisLabel,
  yAxisLabel,
  yAxisBenchmark,
  tooltipRenderer,
}: ScatterProps) => {
  const brandTheme = useBrandChartTheme();
  const averageColor = brandTheme.mode === "light" ? neutral[500] : neutral[300];

  const buildDefinition = useCallback(
    () =>
      buildScatterDefinition({
        data,
        brandTheme,
        averageColor,
        benchmarks: yAxisBenchmark,
        xAxisLabel,
        yAxisLabel,
      }),
    [data, brandTheme, averageColor, yAxisBenchmark, xAxisLabel, yAxisLabel],
  );

  const definition = useMemo(
    () => defineChart({ chart: buildDefinition, tooltip: { use: tooltip } }),
    [buildDefinition],
  );

  const legend = useMemo(
    () => getScatterLegend(averageColor, yAxisBenchmark),
    [averageColor, yAxisBenchmark],
  );

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data)) {
    return (
      <NoData message={noDataMessage} height={minHeight} stretch={stretch} />
    );
  }

  const ariaLabel =
    title ?? [yAxisLabel, xAxisLabel].filter(Boolean).join(" by ");

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel || "Scatter chart"}
        brandTheme={brandTheme}
        buildDefinition={buildDefinition}
        legend={legend}
      />
    );
  }

  return (
    <ErrorBoundary>
      <Flex direction="column" width="100%" height="100%">
        <BrandChart
          definition={definition}
          brandTheme={brandTheme}
          ariaLabel={ariaLabel || "Scatter chart"}
          minHeight={minHeight}
          flexGrow={stretch ? 1 : undefined}
          height={stretch ? undefined : minHeight}
          renderTooltipBody={({ points }) => {
            const item = points[0]?.datum;

            return item && "label" in item
              ? tooltipRenderer(brandTheme.theme.foreground ?? "", item)
              : null;
          }}
        />
        <LineLegend items={legend} />
      </Flex>
    </ErrorBoundary>
  );
};

const Scatter = byLook(BrandScatter, LegacyScatter);

export { Scatter };
